import Popup from 'devextreme-react/popup';
import LoadPanel from 'devextreme-react/load-panel';
import DropDownButton from 'devextreme-react/drop-down-button';
import Scheduler, { Editing, Resource, View, Toolbar, Item, type SchedulerRef } from 'devextreme-react/scheduler';
import type {
  AppointmentAddingEvent,
  AppointmentClickEvent,
  AppointmentFormOpeningEvent,
  CellClickEvent
} from 'devextreme/ui/scheduler';
import { showConfirmDialog, buildSchedulerTypePalette, useResponsivePopupOptions, useShellLayout } from '@jarvis/platform-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useThemeStore } from '../../../theme/useThemeStore';
import { useAuthStore } from '../../../auth/useAuthStore';
import { TimezoneBanner } from '../../../components/TimezoneBanner';
import { ToolbarHintButton } from '../../../components/ToolbarHintButton';
import {
  cancelTimeOff,
  purgeTimeOff,
  bulkCreateTimeOff,
  createTimeOff,
  listCalendarTargets,
  listTimeOff,
  listTimeOffOrgOptions,
  updateTimeOff,
  type CalendarTarget,
  type TimeOffEntry,
  type TimeOffOrgOptions,
  type TimeOffStatus
} from '../../../api/timeOff';
import { useHasRole } from '../../../auth/useHasRole';
import { showSuccessToast } from '../../../notifications/toast';
import { TimeOffEntryCreateContent } from './components/TimeOffEntryCreateContent';
import { TimeOffCalendarAgendaList } from './components/TimeOffCalendarAgendaList';
import { TimeOffCalendarAppointment } from './components/TimeOffCalendarAppointment';
import { TimeOffCalendarLegend } from './components/TimeOffCalendarLegend';
import { TimeOffCalendarMobileFilterBar } from './components/TimeOffCalendarMobileFilterBar';
import { TimeOffCalendarMobileMenu } from './components/TimeOffCalendarMobileMenu';
import { TimeOffCalendarToolbarFilters } from './components/TimeOffCalendarToolbarFilters';
import {
  type TimeOffCalendarOrgFilter as CalendarOrgFilter
} from './components/TimeOffCalendarOrgFilter';
import { TimeOffEntryDetailContent } from './components/TimeOffEntryDetailContent';
import { TimeOffEntryEditContent } from './components/TimeOffEntryEditContent';
import { TimeOffSyncStatusBanner } from './components/TimeOffSyncStatusBanner';
import {
  buildCreateDraftFromSchedulerCell,
  buildDefaultCreateDraft
} from './timeOffCalendarCreateDraft';
import { toApiIso, nextAllDayEnd } from './timeOffDateUtils';
import { timeOffEntryToEditDraft, timeOffEntryToViewModel, type TimeOffEntryCreateDraft, type TimeOffEntryEditDraft } from './timeOffEntryTypes';
import { canMutateTimeOffEntry } from './timeOffEntryMutability';
import { confirmPastTimeOffCreate } from './timeOffPastStartWarning';
import { confirmCompanyHolidayTimeOffCreate } from './timeOffHolidayWarning';
import { confirmNoSameDayTimeOffConflict } from './timeOffSameDayWarning';
import { rowsAfterHolidayExclusion } from './timeOffHolidaySubmit';
import { buildHolidayLookup } from './timeOffHolidayExclusion';
import { useCompanyHolidays } from './useCompanyHolidays';
import { useSchedulerRepaintOnResize } from './useSchedulerRepaintOnResize';
import {
  shouldShowTimeOffEntryForCalendarTargets,
  shouldShowTimeOffEntryInGrid
} from './timeOffEntriesGridFilters';
import { holidayNameForSchedulerDate, dateFromSchedulerDataCellModel, dateFromSchedulerDateCellModel, isSchedulerWeekdayHeaderText, schedulerDayOfMonth } from './timeOffCalendarHolidays';
import {
  calendarEntriesUnchanged,
  calendarTargetsUnchanged,
  useTimeOffCalendarPoll
} from './timeOffCalendarRefresh';
import { useTimeOffCalendarRealtime } from './useTimeOffCalendarRealtime';
import {
  calendarPrefsStorageKey,
  loadCalendarPrefs,
  parseCalendarPrefsDate,
  resolveActiveTargetIds,
  saveCalendarPrefs,
  type TimeOffCalendarEmployeeFilterPref
} from './timeOffCalendarPrefs';
import { computeCalendarFetchRange, computeCalendarMonthGridRange } from './timeOffCalendarRange';
import { filterSchedulerEventsInRange } from './timeOffCalendarAgenda';
import { toSchedulerEvent, type TimeOffSchedulerEvent } from './timeOffCalendarTypes';

type CalendarPopupState =
  | { mode: 'closed' }
  | { mode: 'view'; event: TimeOffSchedulerEvent }
  | { mode: 'edit'; event: TimeOffSchedulerEvent; draft: TimeOffEntryEditDraft }
  | { mode: 'create'; draft: TimeOffEntryCreateDraft };

interface CalendarVisibilityMenuItem {
  id: 'show-past' | 'show-cancelled';
  text: string;
  icon: string;
}

/**
 * Scheduler click payloads often omit custom fields — only `text`, dates, resource fields, etc.
 * Joining arrays or assuming UTC strings breaks the popup render. Resolve the authoritative row here.
 */
function resolveClickedAppointment(raw: unknown, events: TimeOffSchedulerEvent[]): TimeOffSchedulerEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Partial<TimeOffSchedulerEvent>;

  if (data.id !== undefined && data.id !== null && String(data.id) !== '') {
    const byId = events.find((e) => e.id === String(data.id));
    if (byId) return byId;
  }

  const startRaw = data.startDate;
  const endRaw = data.endDate;
  if (startRaw != null && endRaw != null && data.text != null) {
    const start = startRaw instanceof Date ? startRaw : new Date(String(startRaw));
    const end = endRaw instanceof Date ? endRaw : new Date(String(endRaw));
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const byShape = events.find(
        (e) => e.text === data.text && e.startDate.getTime() === start.getTime() && e.endDate.getTime() === end.getTime()
      );
      if (byShape) return byShape;
    }
  }

  return null;
}

function formatSchedulerTimeCell(date: Date): string {
  const minutes = date.getMinutes();
  return date
    .toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: minutes === 0 ? undefined : '2-digit',
      hour12: true
    })
    .replace(/\s/g, ' ');
}

export function TimeOffCalendarPage() {
  const { hasAnyHrRole, isItSysAdmin } = useHasRole();
  const navigate = useNavigate();
  const username = useAuthStore((s) => s.username);
  const itSysAdmin = isItSysAdmin();
  const canEditTimeOff = hasAnyHrRole();
  const canCancelTimeOff = canEditTimeOff;
  const canAddTimeOff = hasAnyHrRole();
  const showCalendarFilters = itSysAdmin;
  const themeFamily = useThemeStore((s) => s.family);
  const themeMode = useThemeStore((s) => s.mode);
  const { holidays, disabledHolidayDates } = useCompanyHolidays();

  const prefsStorageKey = useMemo(() => calendarPrefsStorageKey(username), [username]);
  const initialPrefs = useMemo(() => loadCalendarPrefs(prefsStorageKey), [prefsStorageKey]);
  const prefsHydratedRef = useRef(false);
  const targetsInitializedRef = useRef(false);
  const mobileCalendarBodyRef = useRef<HTMLDivElement | null>(null);
  const mobileCalendarFocusedRef = useRef(false);
  const calendarHostRef = useRef<HTMLElement | null>(null);
  const schedulerRef = useRef<SchedulerRef>(null);

  const [entries, setEntries] = useState<TimeOffEntry[]>([]);
  const [calendarTargets, setCalendarTargets] = useState<CalendarTarget[]>([]);
  const [activeTargets, setActiveTargets] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<CalendarPopupState>({ mode: 'closed' });
  const [currentView, setCurrentView] = useState(initialPrefs.currentView);
  const [currentDate, setCurrentDate] = useState(() => parseCalendarPrefsDate(initialPrefs.currentDateIso));
  const [showPast, setShowPast] = useState(initialPrefs.showPast);
  const [showCancelled, setShowCancelled] = useState(initialPrefs.showCancelled);
  const [employeeFilter, setEmployeeFilter] = useState<TimeOffCalendarEmployeeFilterPref | null>(
    initialPrefs.employeeFilter
  );
  const [orgFilter, setOrgFilter] = useState<CalendarOrgFilter>(initialPrefs.orgFilter);
  const [orgOptions, setOrgOptions] = useState<TimeOffOrgOptions>({ companies: [], departments: [] });
  const [orgOptionsLoading, setOrgOptionsLoading] = useState(true);
  const [popupError, setPopupError] = useState<string | null>(null);
  const [calendarPickerKey, setCalendarPickerKey] = useState(0);
  const [employeePickerKey, setEmployeePickerKey] = useState(0);
  const [actionBusy, setActionBusy] = useState<'idle' | 'save' | 'cancel' | 'create' | 'delete'>('idle');

  const typeColors = useMemo(
    () => Object.values(buildSchedulerTypePalette()),
    [themeFamily, themeMode]
  );

  const holidayLookup = useMemo(() => buildHolidayLookup(holidays), [holidays]);

  const hrCalendarTargets = useMemo(
    () => calendarTargets.filter((t) => t.isEnabled),
    [calendarTargets]
  );

  const popupIsEdit = popup.mode === 'edit';
  const popupIsCreate = popup.mode === 'create';
  const popupOptions = useResponsivePopupOptions({
    width: popupIsEdit || popupIsCreate ? 620 : 560,
    height: 'auto'
  });
  const { isNarrow } = useShellLayout();
  const effectiveView = isNarrow ? 'month' : currentView;
  useSchedulerRepaintOnResize(calendarHostRef, schedulerRef, `${isNarrow}:${effectiveView}`);

  useEffect(() => {
    if (!isNarrow || mobileCalendarFocusedRef.current) return;
    mobileCalendarFocusedRef.current = true;
    const frame = window.requestAnimationFrame(() => {
      mobileCalendarBodyRef.current?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isNarrow]);

  const fetchRange = useMemo(
    () => computeCalendarFetchRange(effectiveView, currentDate),
    [effectiveView, currentDate]
  );

  const listQuery = useMemo(
    () => ({
      from: fetchRange.from,
      to: fetchRange.to,
      take: 1000,
      ...(employeeFilter?.upn ? { employeeUpn: employeeFilter.upn } : {}),
      ...(orgFilter.kind === 'company' ? { company: orgFilter.value } : {}),
      ...(orgFilter.kind === 'department' ? { department: orgFilter.value } : {})
    }),
    [fetchRange, employeeFilter, orgFilter]
  );

  const resetCalendarFilters = useCallback(() => {
    setEmployeeFilter(null);
    setOrgFilter({ kind: 'all' });
  }, []);

  const calendarFilterControls = useMemo(
    () =>
      canEditTimeOff ? (
        <TimeOffCalendarToolbarFilters
          employeeFilter={employeeFilter}
          onEmployeeFilterChange={setEmployeeFilter}
          orgFilter={orgFilter}
          onOrgFilterChange={setOrgFilter}
          orgOptions={orgOptions}
          orgOptionsLoading={orgOptionsLoading}
          onReset={resetCalendarFilters}
        />
      ) : null,
    [
      canEditTimeOff,
      employeeFilter,
      orgFilter,
      orgOptions,
      orgOptionsLoading,
      resetCalendarFilters
    ]
  );

  const applyFetchedCalendarData = useCallback((list: TimeOffEntry[], targets: CalendarTarget[]) => {
    setEntries((prev) => (calendarEntriesUnchanged(prev, list) ? prev : list));
    setCalendarTargets((prev) => (calendarTargetsUnchanged(prev, targets) ? prev : targets));
  }, []);

  const refreshSilent = useCallback(async () => {
    try {
      const [list, targets] = await Promise.all([listTimeOff(listQuery), listCalendarTargets()]);
      applyFetchedCalendarData(list, targets);
    } catch {
      // Background poll — keep current view on transient failures.
    }
  }, [applyFetchedCalendarData, listQuery]);

  const refresh = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const [list, targets] = await Promise.all([listTimeOff(listQuery), listCalendarTargets()]);
      applyFetchedCalendarData(list, targets);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load time off.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [applyFetchedCalendarData, listQuery]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const popupOpen = popup.mode !== 'closed';
  const { connected: calendarRealtimeConnected } = useTimeOffCalendarRealtime({
    enabled: canEditTimeOff,
    popupOpen,
    onCalendarChanged: refreshSilent
  });

  useTimeOffCalendarPoll({
    refreshSilent,
    isNarrow,
    popupOpen,
    signalRConnected: calendarRealtimeConnected
  });

  useEffect(() => {
    let cancelled = false;
    setOrgOptionsLoading(true);
    void listTimeOffOrgOptions()
      .then((options) => {
        if (!cancelled) setOrgOptions(options);
      })
      .catch(() => {
        if (!cancelled) setOrgOptions({ companies: [], departments: [] });
      })
      .finally(() => {
        if (!cancelled) setOrgOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (calendarTargets.length === 0 || targetsInitializedRef.current) return;
    targetsInitializedRef.current = true;
    const saved = loadCalendarPrefs(prefsStorageKey);
    setActiveTargets(resolveActiveTargetIds(saved.activeTargetIds, calendarTargets.map((t) => t.id)));
  }, [calendarTargets, prefsStorageKey]);

  useEffect(() => {
    if (!prefsHydratedRef.current) {
      prefsHydratedRef.current = true;
      return;
    }
    const timer = window.setTimeout(() => {
      saveCalendarPrefs(prefsStorageKey, {
        currentView,
        currentDateIso: currentDate.toISOString(),
        activeTargetIds: [...activeTargets],
        showPast,
        showCancelled,
        employeeFilter,
        orgFilter
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [prefsStorageKey, currentView, currentDate, activeTargets, showPast, showCancelled, employeeFilter, orgFilter]);

  const filteredEvents = useMemo(() => {
    const base = entries.filter((e) =>
      shouldShowTimeOffEntryInGrid(e, { showCancelled, showPast })
    );
    const filtered =
      activeTargets.size === 0
        ? base
        : base.filter((e) => shouldShowTimeOffEntryForCalendarTargets(e, activeTargets));
    return filtered.map((e) => toSchedulerEvent(e));
  }, [entries, activeTargets, showPast, showCancelled]);

  const monthGridRange = useMemo(
    () => (effectiveView === 'month' ? computeCalendarMonthGridRange(currentDate) : null),
    [effectiveView, currentDate]
  );

  const agendaEvents = useMemo(() => {
    if (!monthGridRange) return filteredEvents;
    return filterSchedulerEventsInRange(filteredEvents, monthGridRange.start, monthGridRange.end);
  }, [filteredEvents, monthGridRange]);

  const schedulerDayHours =
    effectiveView === 'month' ? { startDayHour: 0, endDayHour: 24 } : { startDayHour: 6, endDayHour: 20 };

  const visibilityMenuItems = useMemo(
    (): CalendarVisibilityMenuItem[] => [
      {
        id: 'show-past',
        text: 'Show past PTO',
        icon: showPast ? 'check' : 'isblank'
      },
      {
        id: 'show-cancelled',
        text: 'Show cancelled',
        icon: showCancelled ? 'check' : 'isblank'
      }
    ],
    [showPast, showCancelled]
  );

  const handleVisibilityMenuClick = useCallback(
    (id: unknown) => {
      if (id === 'show-past') {
        setShowPast((value) => !value);
      } else if (id === 'show-cancelled') {
        setShowCancelled((value) => !value);
      }
    },
    []
  );

  const openAgendaEvent = useCallback((event: TimeOffSchedulerEvent) => {
    setPopup({ mode: 'view', event });
    setPopupError(null);
  }, []);

  function closePopup() {
    setPopup({ mode: 'closed' });
    setPopupError(null);
  }

  const defaultCreateCalendarIds = useCallback((): string[] => {
    if (itSysAdmin) return [];
    return hrCalendarTargets.length > 0 ? [hrCalendarTargets[0].id] : [];
  }, [hrCalendarTargets, itSysAdmin]);

  const openCreatePopup = useCallback((draft: TimeOffEntryCreateDraft) => {
    setEmployeePickerKey((k) => k + 1);
    setCalendarPickerKey((k) => k + 1);
    setPopup({ mode: 'create', draft });
    setPopupError(null);
  }, []);

  const openAddPopup = useCallback(() => {
    openCreatePopup(buildDefaultCreateDraft(effectiveView, defaultCreateCalendarIds()));
  }, [effectiveView, defaultCreateCalendarIds, openCreatePopup]);

  const openMultiAddPage = useCallback(() => {
    navigate('/hr/time-off/management');
  }, [navigate]);

  function toggleTarget(id: string) {
    setActiveTargets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const onAppointmentClick = useCallback(
    (e: AppointmentClickEvent) => {
      e.cancel = true;
      const resolved = resolveClickedAppointment(e.appointmentData, filteredEvents);
      if (!resolved) return;
      setPopup({ mode: 'view', event: resolved });
      setPopupError(null);
    },
    [filteredEvents]
  );

  const onCellClick = useCallback(
    (e: CellClickEvent) => {
      if (!canAddTimeOff) return;
      e.cancel = true;
      const cell = e.cellData;
      if (!cell?.startDate || !cell?.endDate) return;
      openCreatePopup(
        buildCreateDraftFromSchedulerCell(
          effectiveView,
          { startDate: cell.startDate, endDate: cell.endDate },
          defaultCreateCalendarIds()
        )
      );
    },
    [canAddTimeOff, effectiveView, defaultCreateCalendarIds, openCreatePopup]
  );

  const onAppointmentAdding = useCallback(
    (e: AppointmentAddingEvent) => {
      if (!canAddTimeOff) return;
      e.cancel = true;
      const data = e.appointmentData;
      if (!data?.startDate || !data?.endDate) return;
      const startRaw = data.startDate;
      const endRaw = data.endDate;
      const startDate = startRaw instanceof Date ? startRaw : new Date(String(startRaw));
      const endDate = endRaw instanceof Date ? endRaw : new Date(String(endRaw));
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;
      openCreatePopup(
        buildCreateDraftFromSchedulerCell(
          effectiveView,
          { startDate, endDate },
          defaultCreateCalendarIds()
        )
      );
    },
    [canAddTimeOff, effectiveView, defaultCreateCalendarIds, openCreatePopup]
  );

  function onAppointmentFormOpening(e: AppointmentFormOpeningEvent) {
    e.cancel = true;
  }

  const renderAppointment = useCallback(
    (model: { appointmentData?: TimeOffSchedulerEvent }) => {
      if (!model?.appointmentData) return null;
      return (
        <TimeOffCalendarAppointment appointmentData={model.appointmentData} typeColors={typeColors} />
      );
    },
    [typeColors]
  );

  const renderDateCell = useCallback(
    (model: { date?: Date; text?: string; data?: { date?: Date; text?: string } }) => {
      const { date, text } = dateFromSchedulerDateCellModel(model);
      if (isSchedulerWeekdayHeaderText(text)) {
        return <div className="time-off-calendar-date-cell">{text}</div>;
      }
      if (!date) {
        return <div className="time-off-calendar-date-cell">{text ?? null}</div>;
      }
      const name = holidayNameForSchedulerDate(date, holidayLookup);
      const isHoliday = Boolean(name);
      return (
        <div
          className={`time-off-calendar-date-cell${isHoliday ? ' time-off-calendar-date-cell--holiday' : ''}`}
          title={name}
        >
          <span className="time-off-calendar-date-cell__number">{text ?? String(schedulerDayOfMonth(date))}</span>
        </div>
      );
    },
    [holidayLookup]
  );

  const renderDataCell = useCallback(
    (model: { startDate?: Date; data?: { startDate?: Date } }) => {
      const date = dateFromSchedulerDataCellModel(model);
      if (!date) {
        return <div className="time-off-calendar-data-cell" />;
      }
      const name = holidayNameForSchedulerDate(date, holidayLookup);
      const isHoliday = Boolean(name);
      const dayNumber = schedulerDayOfMonth(date);
      return (
        <div
          className={`time-off-calendar-data-cell${isHoliday ? ' time-off-calendar-data-cell--holiday' : ''}`}
          title={name}
        >
          <span className="time-off-calendar-data-cell__day">{dayNumber}</span>
          {isHoliday && effectiveView === 'month' ? (
            <span className="time-off-calendar-data-cell__holiday-name">{name}</span>
          ) : null}
        </div>
      );
    },
    [holidayLookup, effectiveView]
  );

  const renderTimeCell = useCallback((model: { date?: Date; text?: string }) => {
    if (!(model.date instanceof Date)) {
      return <span>{model.text ?? null}</span>;
    }
    return <span>{formatSchedulerTimeCell(model.date)}</span>;
  }, []);

  function beginEditFromPopup() {
    if (!canEditTimeOff || popup.mode !== 'view') return;
    const entry = entries.find((e) => e.id === popup.event.id);
    if (!entry || !canMutateTimeOffEntry(entry)) return;
    setPopupError(null);
    setEmployeePickerKey((k) => k + 1);
    setCalendarPickerKey((k) => k + 1);
    setPopup({
      mode: 'edit',
      event: popup.event,
      draft: timeOffEntryToEditDraft(entry)
    });
  }

  async function submitEditFromPopup() {
    if (popup.mode !== 'edit') return;
    const { event, draft } = popup;
    const entry = entries.find((e) => e.id === event.id);
    if (!entry) {
      setPopupError('This entry is no longer loaded. Close the dialog and try again.');
      return;
    }
    if (draft.calendarTargetIds.length === 0) {
      setPopupError('Select at least one calendar.');
      return;
    }
    const proceedSameDay = await confirmNoSameDayTimeOffConflict({
      employeeUpn: draft.employeeUpn.trim(),
      excludeEntryId: event.id,
      drafts: [
        {
          startUtc: toApiIso(draft.startLocal, draft.isAllDay),
          endUtc: toApiIso(draft.isAllDay ? nextAllDayEnd(draft.startLocal) : draft.endLocal, draft.isAllDay),
          isAllDay: draft.isAllDay,
          status: draft.status
        }
      ]
    });
    if (!proceedSameDay) return;
    setActionBusy('save');
    setPopupError(null);
    try {
      const saved = await updateTimeOff(event.id, {
        employeeUpn: draft.employeeUpn.trim(),
        employeeDisplayName: draft.employeeDisplayName,
        type: draft.type,
        startUtc: toApiIso(draft.startLocal, draft.isAllDay),
        endUtc: toApiIso(draft.isAllDay ? nextAllDayEnd(draft.startLocal) : draft.endLocal, draft.isAllDay),
        isAllDay: draft.isAllDay,
        notes: draft.notes,
        status: draft.status,
        calendarTargetIds: draft.calendarTargetIds,
        rowVersion: entry.rowVersion
      });
      setEntries((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
      setPopup({ mode: 'view', event: toSchedulerEvent(saved) });
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setActionBusy('idle');
    }
  }

  async function submitCreateFromPopup() {
    if (popup.mode !== 'create') return;
    const { draft } = popup;
    if (!draft.employeeUpn.trim()) {
      setPopupError('Employee is required.');
      return;
    }
    if (draft.calendarTargetIds.length === 0) {
      setPopupError('Select at least one calendar.');
      return;
    }
    if (!draft.isAllDay && draft.endLocal < draft.startLocal) {
      setPopupError('End must not be before Start.');
      return;
    }
    const proceed = await confirmPastTimeOffCreate(draft.startLocal, draft.isAllDay);
    if (!proceed) return;
    const proceedHoliday = await confirmCompanyHolidayTimeOffCreate(
      draft.startLocal,
      draft.endLocal,
      draft.isAllDay,
      holidays
    );
    if (!proceedHoliday) return;

    const excludedRows = rowsAfterHolidayExclusion(
      draft.startLocal,
      draft.endLocal,
      draft.isAllDay,
      holidays,
      { type: draft.type, status: draft.status, notes: draft.notes }
    );
    if (excludedRows.length === 0) {
      setPopupError('All selected days are company holidays.');
      return;
    }

    const proceedSameDay = await confirmNoSameDayTimeOffConflict({
      employeeUpn: draft.employeeUpn.trim(),
      drafts: excludedRows
    });
    if (!proceedSameDay) return;

    setActionBusy('create');
    setPopupError(null);
    try {
      if (excludedRows.length === 1) {
        const saved = await createTimeOff({
          employeeUpn: draft.employeeUpn.trim(),
          employeeDisplayName: draft.employeeDisplayName,
          calendarTargetIds: draft.calendarTargetIds,
          ...excludedRows[0]
        });
        setEntries((prev) => [...prev, saved]);
      } else {
        const result = await bulkCreateTimeOff({
          employeeUpn: draft.employeeUpn.trim(),
          employeeDisplayName: draft.employeeDisplayName,
          calendarTargetIds: draft.calendarTargetIds,
          rows: excludedRows
        });
        if (result.errors.length > 0) {
          setPopupError(result.errors.map((e) => e.message).join(' '));
          return;
        }
        setEntries((prev) => [...prev, ...result.created]);
      }
      closePopup();
      showSuccessToast(
        excludedRows.length === 1
          ? 'Time off created.'
          : `Created ${excludedRows.length} time off entries.`
      );
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : 'Create failed.');
    } finally {
      setActionBusy('idle');
    }
  }

  async function confirmCancelFromPopup() {
    if (popup.mode !== 'view') return;
    const { event } = popup;
    const confirmed = await showConfirmDialog({
      title: 'Cancel time off',
      message:
        'Cancel this time off entry? It will be removed from calendar views and deleted from Outlook on the next sync.',
      confirmText: 'Cancel entry',
      cancelText: 'Keep',
      danger: true
    });
    if (!confirmed) return;
    setActionBusy('cancel');
    setPopupError(null);
    try {
      await cancelTimeOff(event.id);
      setEntries((prev) => prev.filter((r) => r.id !== event.id));
      closePopup();
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : 'Cancel failed.');
    } finally {
      setActionBusy('idle');
    }
  }

  async function confirmDeleteFromPopup() {
    if (popup.mode !== 'view') return;
    const { event } = popup;
    const confirmed = await showConfirmDialog({
      title: 'Delete time off',
      message:
        'Permanently delete this time off? It will be removed from Outlook now and from this site. This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Keep',
      danger: true
    });
    if (!confirmed) return;
    setActionBusy('delete');
    setPopupError(null);
    try {
      await purgeTimeOff(event.id);
      setEntries((prev) => prev.filter((r) => r.id !== event.id));
      closePopup();
      showSuccessToast('Time off entry deleted.');
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setActionBusy('idle');
    }
  }

  function renderPopupContent() {
    if (popup.mode === 'create') {
      return (
        <TimeOffEntryCreateContent
          draft={popup.draft}
          onDraftChange={(updater) =>
            setPopup((prev) => (prev.mode === 'create' ? { ...prev, draft: updater(prev.draft) } : prev))
          }
          error={popupError}
          busy={actionBusy === 'create'}
          itSysAdmin={itSysAdmin}
          hrCalendarTargets={hrCalendarTargets}
          calendarPickerKey={`cal-create-${calendarPickerKey}`}
          employeePickerKey={`emp-create-${employeePickerKey}`}
          onCancel={closePopup}
          onCreate={() => void submitCreateFromPopup()}
          disabledHolidayDates={disabledHolidayDates}
        />
      );
    }

    if (popup.mode === 'edit') {
      return (
        <TimeOffEntryEditContent
          draft={popup.draft}
          onDraftChange={(updater) =>
            setPopup((prev) => (prev.mode === 'edit' ? { ...prev, draft: updater(prev.draft) } : prev))
          }
          error={popupError}
          busy={actionBusy === 'save'}
          itSysAdmin={itSysAdmin}
          hrCalendarTargets={hrCalendarTargets}
          calendarPickerKey={`cal-pick-${popup.event.id}-${calendarPickerKey}`}
          employeePickerKey={`emp-edit-${popup.event.id}-${employeePickerKey}`}
          onDiscard={() => {
            setPopupError(null);
            setPopup({ mode: 'view', event: popup.event });
          }}
          onClose={closePopup}
          onSave={() => void submitEditFromPopup()}
        />
      );
    }

    if (popup.mode !== 'view') return null;

    const { event } = popup;
    const entry = entries.find((e) => e.id === event.id);
    const entryMutable = entry ? canMutateTimeOffEntry(entry) : false;

    const viewModel = entry
      ? timeOffEntryToViewModel(entry)
      : {
          id: event.id,
          employeeUpn: event.employeeUpn,
          employeeDisplayName: event.employeeDisplayName,
          type: event.type,
          status: event.status as TimeOffStatus,
          startUtc: event.startUtc,
          endUtc: event.endUtc,
          isAllDay: event.isAllDay,
          notes: event.notes,
          calendarTargetNames: event.calendarTargetNames ?? [],
          needsEmployeeIdentity: event.needsEmployeeIdentity
        };

    return (
      <>
        {popupError ? (
          <div
            role="alert"
            style={{
              margin: '0.5rem 0.75rem 0',
              padding: '0.5rem 0.65rem',
              borderRadius: 8,
              background: 'var(--ops-chip-danger-bg, #fef2f2)',
              color: 'var(--ops-color-danger, #991b1b)',
              fontSize: 13
            }}
          >
            {popupError}
          </div>
        ) : null}
        <TimeOffEntryDetailContent
          entry={viewModel}
          typeColors={typeColors}
          busyCancel={actionBusy === 'cancel'}
          busyDelete={actionBusy === 'delete'}
          showOpenInAllTimeOff
          onCancel={canCancelTimeOff && entryMutable ? () => void confirmCancelFromPopup() : undefined}
          onDelete={canEditTimeOff ? () => void confirmDeleteFromPopup() : undefined}
          onEdit={canEditTimeOff && entryMutable ? beginEditFromPopup : undefined}
          onClose={closePopup}
          onOpenInAllTimeOff={() => {
            closePopup();
            navigate('/hr/time-off/all');
          }}
        />
      </>
    );
  }

  const popupTitle =
    popup.mode === 'edit' && canEditTimeOff
      ? 'Edit time off'
      : popup.mode === 'create'
        ? 'Add time off'
        : 'Time off details';

  return (
    <div
      className={`ops-fill-page time-off-calendar-page${canAddTimeOff ? ' time-off-calendar-page--can-add' : ''}${isNarrow ? ' time-off-calendar-page--narrow' : ''}`}
    >
      {isNarrow ? (
        <>
          <div className="time-off-calendar-mobile-chrome">
            <div className="time-off-calendar-mobile-chrome__row">
              {canEditTimeOff ? (
                <TimeOffCalendarMobileFilterBar
                  employeeFilter={employeeFilter}
                  onEmployeeFilterChange={setEmployeeFilter}
                  orgFilter={orgFilter}
                  onOrgFilterChange={setOrgFilter}
                  orgOptions={orgOptions}
                  orgOptionsLoading={orgOptionsLoading}
                  onReset={resetCalendarFilters}
                />
              ) : (
                <span className="time-off-calendar-mobile-chrome__title">Time off calendar</span>
              )}
              <TimeOffCalendarMobileMenu
                showPast={showPast}
                onShowPastChange={setShowPast}
                showCancelled={showCancelled}
                onShowCancelledChange={setShowCancelled}
                canAddTimeOff={canAddTimeOff}
                onAdd={openAddPopup}
                addDisabled={loading}
                showCalendarFilters={showCalendarFilters}
                calendarTargets={calendarTargets}
                activeTargets={activeTargets}
                onToggleTarget={toggleTarget}
                onMultiAdd={openMultiAddPage}
                employeeFilter={employeeFilter}
                orgFilter={orgFilter}
                onResetFilters={resetCalendarFilters}
              />
            </div>
            <div className="time-off-calendar-mobile-chrome__meta">
              <TimeOffCalendarLegend typeColors={typeColors} compact />
              <TimezoneBanner prefix="Times in" />
            </div>
          </div>
          <div className="ops-fill-page__alerts time-off-calendar-mobile-alerts">
            <TimeOffSyncStatusBanner entries={entries} variant="alert" />
          </div>
        </>
      ) : (
        <div className="time-off-calendar-chips">
          <div className="time-off-calendar-chips__row time-off-calendar-chips__row--top">
            <TimeOffCalendarLegend typeColors={typeColors} />
              {calendarFilterControls ? (
                <div className="time-off-calendar-chips__filters">{calendarFilterControls}</div>
              ) : null}
            <div className="time-off-calendar-chips__controls">
              {canAddTimeOff ? (
                <>
                  <ToolbarHintButton
                    text="Add"
                    icon="add"
                    stylingMode="contained"
                    type="default"
                    hintTitle="Add time off"
                    hintLines={['Open the add form, or click a day on the calendar to set the start.']}
                    onClick={openAddPopup}
                    disabled={loading}
                  />
                  <ToolbarHintButton
                    text="Multi-Add"
                    icon="event"
                    stylingMode="outlined"
                    hintTitle="Multi-Add PTO"
                    hintLines={['Open the Add New PTO page for multi-date and bulk PTO entry.']}
                    onClick={openMultiAddPage}
                    disabled={loading}
                  />
                </>
              ) : null}
              <DropDownButton
                className="time-off-calendar-options-menu"
                icon="menu"
                stylingMode="outlined"
                hint="Calendar options"
                displayExpr="text"
                keyExpr="id"
                items={visibilityMenuItems}
                dropDownOptions={{ width: 220, showTitle: false }}
                onItemClick={(e) => handleVisibilityMenuClick(e.itemData?.id)}
              />
            </div>
          </div>
          <div className="time-off-calendar-chips__row time-off-calendar-chips__row--meta">
            <div className="time-off-calendar-chips__sync-status">
              <TimeOffSyncStatusBanner entries={entries} />
            </div>
            <div className="time-off-calendar-chips__timezone">
              <TimezoneBanner />
            </div>
          </div>
          {showCalendarFilters ? (
            <div className="time-off-calendar-chips__row time-off-calendar-chips__row--calendars">
              <span className="time-off-calendar-chips__calendars-label">Calendars:</span>
              {calendarTargets.length === 0 ? (
                <span className="time-off-calendar-chips__calendars-empty">
                  No calendar targets configured. An IT SysAdmin can add them via the API.
                </span>
              ) : (
                <div className="time-off-calendar-chips__calendar-list">
                  {calendarTargets.map((t) => {
                    const active = activeTargets.has(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`time-off-calendar-chips__calendar-pill${active ? ' time-off-calendar-chips__calendar-pill--active' : ''}`}
                        onClick={() => toggleTarget(t.id)}
                      >
                        {t.displayName}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {error ? (
        <div className="ops-fill-page__alerts">
          <div style={{ color: 'var(--ops-color-danger, #b91c1c)', fontWeight: 600 }}>{error}</div>
        </div>
      ) : null}

      <div
        ref={isNarrow ? mobileCalendarBodyRef : undefined}
        className={`time-off-calendar-body${isNarrow ? ' time-off-calendar-body--mobile' : ''}`}
      >
        <section
          ref={calendarHostRef}
          className="ops-fill-page__panel time-off-calendar-host"
          style={{ padding: 0, overflow: 'hidden', position: 'relative' }}
        >
          <LoadPanel visible={loading} shading showPane={false} position={{ of: '.time-off-calendar-host' }} />
          <Scheduler
          ref={schedulerRef}
          dataSource={filteredEvents}
          currentView={effectiveView}
          currentDate={currentDate}
          onOptionChanged={(e) => {
            if (!isNarrow && e.name === 'currentView' && typeof e.value === 'string') {
              setCurrentView(e.value);
            }
            if (e.name === 'currentDate' && e.value instanceof Date) {
              setCurrentDate(e.value);
            }
          }}
          startDayHour={schedulerDayHours.startDayHour}
          endDayHour={schedulerDayHours.endDayHour}
          height="100%"
          adaptivityEnabled={false}
          firstDayOfWeek={0}
          textExpr="text"
          startDateExpr="startDate"
          endDateExpr="endDate"
          allDayExpr="allDay"
          appointmentRender={renderAppointment}
          dateCellRender={renderDateCell}
          dataCellRender={renderDataCell}
          timeCellRender={renderTimeCell}
          onAppointmentClick={onAppointmentClick}
          onAppointmentAdding={onAppointmentAdding}
          onAppointmentFormOpening={onAppointmentFormOpening}
          onCellClick={onCellClick}
        >
          <Toolbar>
            <Item name="dateNavigator" />
            <Item name="today" options={{ stylingMode: 'text' }} />
            {!isNarrow ? <Item name="viewSwitcher" /> : null}
          </Toolbar>
          <View type="day" />
          <View type="week" />
          <View type="workWeek" />
          <View type="month" />
          <Editing
            allowAdding={canAddTimeOff}
            allowDeleting={false}
            allowDragging={false}
            allowResizing={false}
            allowUpdating={false}
          />
          <Resource fieldExpr="type" allowMultiple={false} dataSource={typeColors} label="Type" useColorAsDefault />
        </Scheduler>
        </section>
        {isNarrow ? (
          <TimeOffCalendarAgendaList
            events={agendaEvents}
            typeColors={typeColors}
            onSelectEvent={openAgendaEvent}
            compact
            monthLabel={currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          />
        ) : null}
      </div>

      <Popup
        visible={popup.mode !== 'closed'}
        onHiding={closePopup}
        title={popupTitle}
        showCloseButton
        dragEnabled
        width={popupOptions.width}
        height={popupOptions.height}
        fullScreen={popupOptions.fullScreen}
        deferRendering={false}
        shading
        contentRender={() => renderPopupContent()}
      />
    </div>
  );
}
